/**
 * Calendar Link Generator Utility
 * Generates "Add to Calendar" links for Google Calendar, Apple Calendar (ICS), and Outlook
 */

/**
 * Format date for Google Calendar URL (YYYYMMDDTHHmmssZ)
 */
const formatDateForGoogle = (date) => {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
};

/**
 * Format date for ICS file (YYYYMMDDTHHMMSS)
 */
const formatDateForICS = (date) => {
  return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z/, '');
};

/**
 * Generate Google Calendar URL
 * @param {Object} event - Event details
 * @param {string} event.title - Event title
 * @param {string} event.description - Event description
 * @param {string} event.location - Event location
 * @param {Date} event.startDate - Start date/time
 * @param {Date} event.endDate - End date/time
 * @param {string} event.recurrence - Optional recurrence rule (e.g., "WEEKLY")
 * @returns {string} Google Calendar URL
 */
export const generateGoogleCalendarUrl = ({ title, description, location, startDate, endDate, recurrence }) => {
  const baseUrl = 'https://calendar.google.com/calendar/render';
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: title,
    details: description || '',
    location: location || '',
    dates: `${formatDateForGoogle(startDate)}/${formatDateForGoogle(endDate)}`
  });

  // Add recurrence if specified
  if (recurrence) {
    params.append('recur', `RRULE:FREQ=${recurrence};COUNT=12`);
  }

  return `${baseUrl}?${params.toString()}`;
};

/**
 * Generate Outlook Calendar URL
 */
export const generateOutlookCalendarUrl = ({ title, description, location, startDate, endDate }) => {
  const baseUrl = 'https://outlook.live.com/calendar/0/deeplink/compose';
  const params = new URLSearchParams({
    subject: title,
    body: description || '',
    location: location || '',
    startdt: startDate.toISOString(),
    enddt: endDate.toISOString(),
    path: '/calendar/action/compose',
    rru: 'addevent'
  });

  return `${baseUrl}?${params.toString()}`;
};

/**
 * Generate ICS file content for Apple Calendar and other apps
 */
export const generateICSContent = ({ title, description, location, startDate, endDate, recurrence }) => {
  const uid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}@upaschool.com`;
  
  let icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//UPA School//Class Schedule//EN
BEGIN:VEVENT
UID:${uid}
DTSTAMP:${formatDateForICS(new Date())}Z
DTSTART:${formatDateForICS(startDate)}Z
DTEND:${formatDateForICS(endDate)}Z
SUMMARY:${title}
DESCRIPTION:${(description || '').replace(/\n/g, '\\n')}
LOCATION:${location || ''}`;

  if (recurrence) {
    icsContent += `\nRRULE:FREQ=${recurrence};COUNT=12`;
  }

  icsContent += `
END:VEVENT
END:VCALENDAR`;

  return icsContent;
};

/**
 * Download ICS file
 */
export const downloadICSFile = (event, filename = 'class-schedule.ics') => {
  const icsContent = generateICSContent(event);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Generate calendar event from class schedule
 * @param {Object} classData - Class data
 * @param {Object} schedule - Schedule data (day, time)
 * @param {Date} startDate - First class date (optional, defaults to next occurrence)
 * @returns {Object} Event object for calendar functions
 */
export const createClassEvent = (classData, schedule, startDate = null) => {
  const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Parse start time
  const [hours, minutes] = (schedule.start_time || '09:00').split(':').map(Number);
  const durationMinutes = classData.duration || 60;
  
  // Calculate next occurrence if no start date provided
  if (!startDate) {
    startDate = new Date();
    const targetDay = schedule.day_of_week;
    const currentDay = startDate.getDay();
    const daysUntilTarget = (targetDay - currentDay + 7) % 7 || 7;
    startDate.setDate(startDate.getDate() + daysUntilTarget);
  }
  
  // Set time
  startDate.setHours(hours, minutes, 0, 0);
  
  // Calculate end time
  const endDate = new Date(startDate);
  endDate.setMinutes(endDate.getMinutes() + durationMinutes);
  
  return {
    title: `${classData.name} - UPA School`,
    description: `${classData.name} class at Universal Performing Arts.\n\n${classData.description || ''}\n\nAge: ${classData.age_min}-${classData.age_max} years\nDuration: ${durationMinutes} minutes`,
    location: schedule.room_name ? `${schedule.room_name}, Kents Hill Secondary School, Milton Keynes MK7 6BZ` : 'Kents Hill Secondary School, Milton Keynes MK7 6BZ',
    startDate,
    endDate,
    recurrence: 'WEEKLY'
  };
};

/**
 * Generate all calendar links for an event
 */
export const getAllCalendarLinks = (event) => {
  return {
    google: generateGoogleCalendarUrl(event),
    outlook: generateOutlookCalendarUrl(event),
    downloadICS: () => downloadICSFile(event, `${event.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.ics`)
  };
};

export default {
  generateGoogleCalendarUrl,
  generateOutlookCalendarUrl,
  generateICSContent,
  downloadICSFile,
  createClassEvent,
  getAllCalendarLinks
};
